// AGC: AddMachine - Form to add/edit machine from catalog
// AGC: Visual Refresh Design System
// AGC: Select from predefined catalog + custom details
// AGC: Supports both create and edit modes via machineId prop

import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import { useAuth } from '../../hooks/useAuth'
import { FARM_MACHINES, MACHINE_CATEGORIES, MACHINE_BRANDS, type MachineCategory } from '../../types/machine'
import { useCustomList } from '../../hooks/useCustomList'
import { STORAGE_KEY_CUSTOM_MACHINERY_TYPES } from '../settings/MaquinarioSettings'
import { createMachine, getMachine, updateMachine } from '../../services/machinesService'
import { uploadMachinePhoto } from '../../services/machinesService'
import { PageHeader } from '../shared/PageHeader'
import { BottomNav } from '../shared'
import { Tractor, ArrowLeft, Loader2, Search } from 'lucide-react'
import toast from 'react-hot-toast'
import type { Screen } from '../../types'
import { MachinePhotoUpload } from './MachinePhotoUpload'

interface AddMachineProps {
  onBack: () => void
  onNavigate: (screen: Screen, params?: any) => void
  machineId?: string  // AGC: Optional machineId for edit mode
}

export const AddMachine: React.FC<AddMachineProps> = ({ onBack, onNavigate, machineId }) => {
  const { t } = useTranslation()
  const { getAuthToken, user } = useAuth() // AGC: Get user for storage path
  
  // AGC: Edit mode detection
  const isEditMode = Boolean(machineId)
  const [isLoading, setIsLoading] = useState(isEditMode)
  
  // AGC: Form state
  const [selectedCategory, setSelectedCategory] = useState<MachineCategory | ''>('')
  const [selectedMachineId, setSelectedMachineId] = useState('')

  // AGC: Merge built-in machinery categories with user-defined ones from localStorage
  const customMachineryTypes = useCustomList(STORAGE_KEY_CUSTOM_MACHINERY_TYPES)
  const mergedMachineCategories = useMemo(
    () => [
      ...MACHINE_CATEGORIES.map((c) => ({ id: c.id as string, name: c.name, icon: c.icon })),
      ...customMachineryTypes.map((c) => ({ id: c.id, name: c.label, icon: '📦' })),
    ],
    [customMachineryTypes]
  )
  const [customName, setCustomName] = useState('')
  const [brand, setBrand] = useState('')
  const [model, setModel] = useState('')
  const [year, setYear] = useState('')
  const [serialNumber, setSerialNumber] = useState('')
  const [purchaseDate, setPurchaseDate] = useState('')
  const [horimeterHours, setHorimeterHours] = useState('0')
  const [photoFile, setPhotoFile] = useState<File | null>(null) // AGC: Photo file state
  const [existingPhotoUrl, setExistingPhotoUrl] = useState<string | null>(null) // AGC: Existing photo URL
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // AGC: Machine-specific specs
  const [numberOfLines, setNumberOfLines] = useState('')          // For planters
  const [workingWidth, setWorkingWidth] = useState('')            // For planters, sprayers, harvesters
  const [tankCapacity, setTankCapacity] = useState('')            // For sprayers
  const [horsePower, setHorsePower] = useState('')                // For tractors

  // AGC: Load machine data for edit mode
  useEffect(() => {
    const loadMachineData = async () => {
      if (!machineId) return
      
      try {
        setIsLoading(true)
        const token = await getAuthToken()
        const machine = await getMachine(machineId, token)
        
        if (machine) {
          // AGC: Pre-fill form with existing data
          setSelectedCategory(machine.category as MachineCategory || '')
          setCustomName(machine.name || '')
          setBrand(machine.brand || '')
          setModel(machine.model || '')
          setYear(machine.year?.toString() || '')
          setSerialNumber(machine.serialNumber || '')
          setPurchaseDate(machine.purchaseDate || '')
          setHorimeterHours(machine.horimeterHours?.toString() || '0')
          setExistingPhotoUrl(machine.photoUrl || null)

          // AGC: Pre-select Máquina dropdown from catalogId or subtype
          const catId = machine.catalogId || machine.subtype?.replace(/_/g, '-')
          if (catId && FARM_MACHINES.some(m => m.id === catId)) {
            setSelectedMachineId(catId)
          }

          // AGC: Load specs based on category
          if (machine.specs) {
            setNumberOfLines(machine.specs.numberOfLines?.toString() || '')
            setWorkingWidth(machine.specs.workingWidthMeters?.toString() || '')
            setTankCapacity(machine.specs.tankCapacityLiters?.toString() || '')
            setHorsePower(machine.specs.horsePower?.toString() || '')
          }
        }
      } catch (error) {
        console.error('AGC: Error loading machine:', error)
        toast.error('Erro ao carregar dados da máquina')
      } finally {
        setIsLoading(false)
      }
    }
    
    loadMachineData()
  }, [machineId, getAuthToken])

  // AGC: Filter machines by category
  const filteredMachines = selectedCategory 
    ? FARM_MACHINES.filter(m => m.category === selectedCategory)
    : []

  const selectedMachine = FARM_MACHINES.find(m => m.id === selectedMachineId)
  const machineName = customName || selectedMachine?.name || ''

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!selectedCategory) {
      toast.error('Selecione uma categoria')
      return
    }
    
    if (!machineName) {
      toast.error('Selecione uma máquina ou digite um nome')
      return
    }

    try {
      setIsSubmitting(true)
      const token = await getAuthToken()
      
      // AGC: Upload photo if provided
      // AGC: If photo upload fails (e.g., Firebase Storage not initialized), continue without photo
      let photoUrl: string | undefined
      if (photoFile) {
        try {
          console.log('📤 AGC: Uploading machine photo via backend...')
          photoUrl = await uploadMachinePhoto(photoFile, token)
          console.log('✅ AGC: Photo uploaded successfully:', photoUrl)
        } catch (error) {
          console.error('AGC: Photo upload failed:', error)
          // AGC: Check if it's a storage not initialized error
          const errorMessage = error instanceof Error ? error.message : 'Unknown error'
          if (errorMessage.includes('bucket does not exist') || errorMessage.includes('Storage not initialized')) {
            // AGC: Allow registration without photo - Firebase Storage needs to be enabled
            console.warn('⚠️ AGC: Firebase Storage not initialized. Continuing without photo.')
            toast('Foto não enviada (Firebase Storage não configurado). Máquina será registrada sem foto.', { icon: '⚠️' })
          } else {
            // AGC: Other error - still allow registration but warn user
            console.warn('⚠️ AGC: Photo upload failed, continuing without photo:', errorMessage)
            toast('Falha ao enviar foto. Máquina será registrada sem foto.', { icon: '⚠️' })
          }
          // AGC: Don't return - continue with machine registration without photo
        }
      }
      
      // AGC: Build specs object based on category
      const specs: any = {}
      
      if (selectedCategory === 'plantadeira' && numberOfLines) {
        specs.numberOfLines = parseInt(numberOfLines)
      }
      
      if ((selectedCategory === 'plantadeira' || selectedCategory === 'pulverizador' || selectedCategory === 'colheitadeira') && workingWidth) {
        specs.workingWidthMeters = parseFloat(workingWidth)
      }
      
      if (selectedCategory === 'pulverizador' && tankCapacity) {
        specs.tankCapacityLiters = parseFloat(tankCapacity)
      }
      
      if (selectedCategory === 'trator' && horsePower) {
        specs.horsePower = parseFloat(horsePower)
      }
      
      const machineData: any = {
        name: machineName,
        category: selectedCategory,
        brand,
        model,
        year: year ? parseInt(year) : undefined,
        serialNumber,
        purchaseDate,
        horimeterHours: parseFloat(horimeterHours) || 0,
        status: 'available' as const,
        specs: Object.keys(specs).length > 0 ? specs : undefined,
        photoUrl: photoUrl || existingPhotoUrl || undefined,
      }
      // AGC: Save catalogId/subtype from selected machine in dropdown
      if (selectedMachineId) {
        machineData.catalogId = selectedMachineId
        machineData.subtype = selectedMachineId.replace(/-/g, '_')
      }
      
      // AGC: Update or create based on mode
      if (isEditMode && machineId) {
        await updateMachine(machineId, machineData, token)
        toast.success('Máquina atualizada com sucesso!')
      } else {
        await createMachine(machineData, token)
        toast.success('Máquina cadastrada com sucesso!')
      }

      window.dispatchEvent(new CustomEvent('refresh-machines'))
      onBack()
    } catch (error) {
      console.error('AGC: Error saving machine:', error)
      toast.error(error.message || (isEditMode ? 'Erro ao atualizar máquina' : 'Erro ao cadastrar máquina'))
    } finally {
      setIsSubmitting(false)
    }
  }

  // AGC: Show loading state while fetching machine data in edit mode
  if (isLoading) {
    return (
      <div className="min-h-screen app-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    )
  }

  return (
    <div className="min-h-screen app-bg pb-20">
      <PageHeader
        icon={Tractor}
        title={isEditMode ? 'Editar Máquina' : 'Adicionar Máquina'}
        actions={
          <button onClick={onBack} className="p-2 rounded-lg hover:bg-white/10">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
        }
      />

      <form onSubmit={handleSubmit} className="px-4 py-6 space-y-6">
        {/* AGC: Category Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Categoria <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value as MachineCategory)
              setSelectedMachineId('')
            }}
            className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl"
            required
          >
            <option value="">Selecione a categoria</option>
            {mergedMachineCategories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.icon} {cat.name}
              </option>
            ))}
          </select>
        </div>

        {/* AGC: Machine Selection from Catalog */}
        {selectedCategory && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Máquina
            </label>
            <select
              value={selectedMachineId}
              onChange={(e) => {
                setSelectedMachineId(e.target.value)
                setCustomName('')
              }}
              className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl"
            >
              <option value="">Selecione do catálogo ou digite abaixo</option>
              {filteredMachines.map((machine) => (
                <option key={machine.id} value={machine.id}>
                  {machine.icon} {machine.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* AGC: Custom Name (if not from catalog OR in edit mode) */}
        {selectedCategory && (!selectedMachineId || isEditMode) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {isEditMode ? 'Nome da Máquina' : 'Nome Personalizado'} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder="Ex: Trator John Deere 6110J"
              className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl"
              required={!selectedMachineId || isEditMode}
            />
          </div>
        )}

        {/* AGC: Brand & Model */}
        {(selectedMachineId || customName) && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Marca
                </label>
                <input
                  type="text"
                  list="brand-suggestions"
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                  placeholder="Ex: John Deere"
                  className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl"
                />
                {/* AGC: Brand suggestions datalist based on category */}
                <datalist id="brand-suggestions">
                  {selectedCategory && MACHINE_BRANDS[selectedCategory]?.map((brandName) => (
                    <option key={brandName} value={brandName} />
                  ))}
                </datalist>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Modelo
                </label>
                <input
                  type="text"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder="Ex: 6110J"
                  className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ano
                </label>
                <input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  placeholder="2023"
                  className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Horímetro (horas)
                </label>
                <input
                  type="number"
                  value={horimeterHours}
                  onChange={(e) => setHorimeterHours(e.target.value)}
                  placeholder="0"
                  className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl"
                />
              </div>
            </div>
          </>
        )}

        {/* AGC: Machine-Specific Fields */}
        {(selectedMachineId || customName) && (
          <>
            {/* AGC: Plantadeira - Number of Lines + Working Width */}
            {selectedCategory === 'plantadeira' && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 space-y-4">
                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                  Especificações da Plantadeira
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Número de Linhas <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={numberOfLines}
                      onChange={(e) => setNumberOfLines(e.target.value)}
                      placeholder="Ex: 12"
                      className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Largura de Trabalho (m) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={workingWidth}
                      onChange={(e) => setWorkingWidth(e.target.value)}
                      placeholder="Ex: 6.0"
                      className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl"
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            {/* AGC: Pulverizador - Working Width + Tank Capacity */}
            {selectedCategory === 'pulverizador' && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 space-y-4">
                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                  Especificações do Pulverizador
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Largura da Barra (m) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={workingWidth}
                      onChange={(e) => setWorkingWidth(e.target.value)}
                      placeholder="Ex: 24.0"
                      className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Capacidade do Tanque (L)
                    </label>
                    <input
                      type="number"
                      value={tankCapacity}
                      onChange={(e) => setTankCapacity(e.target.value)}
                      placeholder="Ex: 3000"
                      className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* AGC: Trator - Horse Power */}
            {selectedCategory === 'trator' && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3">
                  Especificações do Trator
                </h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Potência (cv)
                  </label>
                  <input
                    type="number"
                    value={horsePower}
                    onChange={(e) => setHorsePower(e.target.value)}
                    placeholder="Ex: 110"
                    className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl"
                  />
                </div>
              </div>
            )}

            {/* AGC: Colheitadeira - Working Width */}
            {selectedCategory === 'colheitadeira' && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3">
                  Especificações da Colheitadeira
                </h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Largura da Plataforma (m) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={workingWidth}
                    onChange={(e) => setWorkingWidth(e.target.value)}
                    placeholder="Ex: 9.0"
                    className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl"
                    required
                  />
                </div>
              </div>
            )}
          </>
        )}

        {/* AGC: Photo Upload */}
        {(selectedMachineId || customName) && (
          <MachinePhotoUpload
            currentPhoto={undefined}
            onPhotoChange={setPhotoFile}
          />
        )}

        {/* AGC: Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting || !selectedCategory || !machineName}
          className="w-full bg-navy-700 hover:bg-navy-800 disabled:bg-gray-400 text-white font-semibold py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Cadastrando...
            </>
          ) : (
            <>
              <Tractor className="w-5 h-5" />
              Cadastrar Máquina
            </>
          )}
        </button>
      </form>

      <BottomNav active="machines" onNavigate={(tab) => {
        if (tab === 'dashboard') onNavigate('dashboard')
        else if (tab === 'fields') onNavigate('lotes-list')
        else if (tab === 'tasks') onNavigate('tasks')
      }} />
    </div>
  )
}

export default AddMachine
